import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import CheckIcon from '@mui/icons-material/Check';

import { convertUnixTimeToMoment, mergePublicAndPrivateItems } from '../../common/ItemTools';
import {
  BORDER, HOVER_BG, TEXT_DARK, TEXT_SECONDARY,
} from '../common/tokens';

// A list has no color of its own, so one is derived from its id — stable
// across sessions, and enough variety that adjacent rows rarely collide.
const SWATCH_PALETTE = [
  '#F55139', '#1565c0', '#4CAF50', '#6a1b9a', '#ff9800',
  '#00838f', '#283593', '#b71c1c', '#0277bd', '#4a148c',
  '#e65100', '#162032', '#1a237e', '#bf360c', '#0d1b2a',
];

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    // eslint-disable-next-line no-bitwise -- djb2-style hash multiply by 33
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    // eslint-disable-next-line no-bitwise -- keep it in int32
    hash |= 0;
  }
  return Math.abs(hash);
}

function swatchColor(listId) {
  if (!listId) return SWATCH_PALETTE[0];
  return SWATCH_PALETTE[hashString(listId) % SWATCH_PALETTE.length];
}

function initials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.slice(0, 2).map((p) => p[0]).join('');
  return letters.toUpperCase();
}

function relativeWhen(seconds) {
  if (!seconds) return null;
  return convertUnixTimeToMoment(seconds);
}

// The picker fetches lists in whatever shape the slice hands back — merged,
// private-only, or public-only — so every field is looked for in all three.
function readListMeta(listInfo) {
  const merged = mergePublicAndPrivateItems(listInfo) || {};
  const stats = merged.stats || listInfo.privateData?.stats || listInfo.publicData?.stats || {};
  const totalItems = stats.totalItems
    || merged.totalItems
    || listInfo.privateData?.totalItems
    || listInfo.publicData?.totalItems
    || 0;
  const time = merged.timeLatest
    || merged.timeCreated
    || listInfo.privateData?.timeLatest
    || listInfo.privateData?.timeCreated
    || listInfo.publicData?.timeLatest
    || listInfo.publicData?.timeCreated
    || 0;
  return {
    title: merged.title || '(Untitled)',
    totalItems,
    when: relativeWhen(time),
  };
}

const Row = styled('li')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 16px',
  cursor: 'pointer',
  transition: 'background-color 0.1s',
  '&:hover': { backgroundColor: HOVER_BG },
});

const Swatch = styled(Box)({
  width: 28,
  height: 28,
  borderRadius: 6,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1,
});

const RowBody = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const RowName = styled('div')({
  fontSize: 13.5,
  fontWeight: 500,
  color: TEXT_DARK,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const RowMeta = styled('div')({
  fontSize: 11.5,
  color: TEXT_SECONDARY,
  marginTop: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const Checkbox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'checked',
})(({ checked, theme }) => ({
  width: 18,
  height: 18,
  borderRadius: 4,
  border: `1.5px solid ${checked ? theme.palette.primary.main : BORDER}`,
  backgroundColor: checked ? theme.palette.primary.main : 'transparent',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background-color 0.1s, border-color 0.1s',
  '& svg': { fontSize: 12, color: '#fff' },
}));

/** One list in the picker: its swatch, name, size and when it was last touched. */
export default function ListRow({ listInfo, checked, onToggle }) {
  const meta = readListMeta(listInfo);
  const color = swatchColor(listInfo.id);
  return (
    <Row
      role="button"
      aria-pressed={checked}
      onClick={() => onToggle(listInfo)}
    >
      <Swatch sx={{ backgroundColor: color }}>{initials(meta.title)}</Swatch>
      <RowBody>
        <RowName>{meta.title}</RowName>
        <RowMeta>
          {`${meta.totalItems} item${meta.totalItems === 1 ? '' : 's'}`}
          {meta.when ? ` · ${meta.when}` : ''}
        </RowMeta>
      </RowBody>
      <Checkbox checked={checked}>
        {checked && <CheckIcon />}
      </Checkbox>
    </Row>
  );
}

ListRow.propTypes = {
  listInfo: PropTypes.shape({ id: PropTypes.string }).isRequired,
  checked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};
